import './TicketForm.scss';

import { useResourceParams } from '@refinedev/core';
import { Form, FormProps } from 'antd';
import { useTranslation } from 'react-i18next';

import { FailReportDataUpdate } from '@/model/FailReport/FailReport';
import FileData from '@/model/General/FileData';
import { TicketCsatSurveyOverride } from '@/model/Ticket/Ticket';
import { TICKET_STATE } from '@/model/Ticket/TicketConstants';
import { normalizeHtmlWhitespace } from '@/utils/ensureHtml';
// y el bloque empresa+info por CompanySelectField para eliminar duplicidad en 4 formularios.
import { createFinishFailedHandler } from '@/utils/formHelpers';

import TicketFormDescription from './TicketFormDescription';
import TicketFormFailInfo from './TicketFormFailInfo';
import TicketFormGeneralInfo from './TicketFormGeneralInfo';
import { useTicketForm } from './useTicketForm';

interface TicketFormProps {
  formProps: FormProps;
  fileList: FileData[];
  setFileList: (files: FileData[]) => void;
  getSubmitStatus?: () => TICKET_STATE | undefined;
  getCsatOverride?: () => TicketCsatSurveyOverride | undefined;
  onSubmitFailed?: () => void;
  setBlockTabs?: (block: boolean) => void;
}

const TicketForm = (props: TicketFormProps) => {
  const { t } = useTranslation();
  const { action } = useResourceParams();
  const {
    formProps,
    fileList,
    setFileList,
    getSubmitStatus,
    onSubmitFailed,
    setBlockTabs,
    getCsatOverride,
  } = props;
  const edit = action === 'edit';
  const {
    companySelectProps,
    subCategorySelectProps,
    usersSelectProps,
    subStatusSelectProps,
    machineSelectProps,
    refTicketsSelectProps,
    formInitialValues,
    clearDraft,
    needFailReportData,
    failCategorySelectProps,
    failReportInitialItems,
  } = useTicketForm(formProps, edit, setBlockTabs);

  return (
    <Form
      {...formProps}
      initialValues={formInitialValues}
      layout="vertical"
      className="TicketForm"
      onValuesChange={(changedValues, allValues) => {
        // Preserva el hook interno de refine para el warnWhenUnsavedChanges
        formProps.onValuesChange?.(changedValues, allValues);

        if (!formProps.form?.isFieldsTouched()) {
          return;
        }

        setBlockTabs?.(true);
      }}
      // Mejora: handler genérico que reemplaza el bloque onFinishFailed duplicado en 4 formularios
      onFinishFailed={createFinishFailedHandler(t, onSubmitFailed)}
      onFinish={values => {
        const submitStatus = getSubmitStatus?.();
        const csatOverride = getCsatOverride?.();
        // Normaliza espacios no separables innecesarios antes de persistir el HTML del editor.
        const normalizedDescription = normalizeHtmlWhitespace(values.description);
        const wrappedDescription = normalizedDescription
          ? `<div id="description">${normalizedDescription}</div>`
          : undefined;
        // Los campos del detalle de falla se anidan en `fail_report_data_update`
        // (contrato del PATCH del ticket que actualiza su reporte automático) y se
        // sacan del nivel raíz para no enviarlos como atributos del ticket.
        const {
          fail_report_category,
          fail_report_warranty,
          fail_report_comment,
          fail_report_items,
          ...ticketValues
        } = values;
        const failReportDataUpdate: FailReportDataUpdate | undefined = needFailReportData
          ? {
              category: fail_report_category,
              warranty: fail_report_warranty,
              comment: fail_report_comment,
              fail_report_items: fail_report_items,
            }
          : undefined;

        formProps.onFinish?.({
          ...ticketValues,
          ...(submitStatus ? { status: submitStatus } : {}),
          ...(csatOverride ?? {}),
          description: wrappedDescription,
          ...(failReportDataUpdate ? { fail_report_data_update: failReportDataUpdate } : {}),
        });
        clearDraft?.();
      }}>
      <TicketFormGeneralInfo
        formProps={formProps}
        formInitialValues={formInitialValues}
        edit={edit}
        companySelectProps={companySelectProps}
        subCategorySelectProps={subCategorySelectProps}
        machineSelectProps={machineSelectProps}
        refTicketsSelectProps={refTicketsSelectProps}
        usersSelectProps={usersSelectProps}
        subStatusSelectProps={subStatusSelectProps}
      />
      {needFailReportData && (
        <TicketFormFailInfo
          failCategorySelectProps={failCategorySelectProps}
          initialItems={failReportInitialItems}
        />
      )}
      <TicketFormDescription fileList={fileList} setFileList={setFileList} />
    </Form>
  );
};

export default TicketForm;
